/**
 * @fileoverview Shared score-update calls for the teacher assessment drawers.
 *
 * The lesson assessment (AR-539, `session_meetings/:id/update_assessment_scores`)
 * and the project final assessment (AR-579,
 * `projects/:id/update_final_assessment_scores`) share ONE PATCH contract:
 *
 *   - Individual mode → send the single-score id (key varies by scope) + values.
 *   - Bulk mode       → send `lv4ItemId` (+ optional `studentIds[]`) + values.
 *   - Values          → any of `masterScore` / `doNotAssess` / `justification`.
 *   - Response        → `{ updatedCount, updatedIds }`.
 *
 * (camelCase keys are converted to snake_case by the humps request interceptor.)
 *
 * The only differences between the two endpoints are the URL and the individual
 * id field name, so both are injected via `createScoreUpdater`. See
 * `docs/api/arc-mosaico-project-final-assessment-scores.md` in the backend.
 */

import { api } from 'helpers'

export interface UpdateScoresResponse {
  updatedCount: number
  updatedIds: string[]
}

/** Values applied to one or many scores. All optional; only sent when defined. */
export interface ScoreValues {
  masterScore?: number
  doNotAssess?: boolean
  justification?: string
}

function applyValues(
  body: Record<string, unknown>,
  { masterScore, doNotAssess, justification }: ScoreValues,
): void {
  if (masterScore !== undefined) body.masterScore = masterScore
  if (doNotAssess !== undefined) body.doNotAssess = doNotAssess
  if (justification !== undefined) body.justification = justification
}

/**
 * Binds the shared individual + bulk score updaters to a concrete endpoint.
 *
 * @param url        PATCH endpoint that accepts both modes.
 * @param scoreIdKey Request key for the single-score id — `assessmentScoreId`
 *                   for lessons, `finalAssessmentScoreId` for project finals.
 */
export function createScoreUpdater(url: string, scoreIdKey: string) {
  /** Individual mode: update a single score by its id. */
  async function updateIndividual(scoreId: string, values: ScoreValues): Promise<UpdateScoresResponse> {
    const body: Record<string, unknown> = { [scoreIdKey]: scoreId }
    applyValues(body, values)
    const { data } = await api.patch(url, body)
    return data as UpdateScoresResponse
  }

  /** Bulk mode: apply the same values to every score of `lv4ItemId`,
   *  optionally narrowed to `studentIds`. */
  async function updateBulk(
    lv4ItemId: string,
    studentIds: string[] | undefined,
    values: ScoreValues,
  ): Promise<UpdateScoresResponse> {
    const body: Record<string, unknown> = { lv4ItemId }
    if (studentIds && studentIds.length > 0) body.studentIds = studentIds
    applyValues(body, values)
    const { data } = await api.patch(url, body)
    return data as UpdateScoresResponse
  }

  return { updateIndividual, updateBulk }
}
